import React, { memo, useEffect, useMemo, useState } from "react";
import { Avatar, Dialog, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";
import { db, auth, storage } from "../api/firebase";
import { useNotification } from "../redux/useNotification";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import { ja } from "date-fns/locale";
import Linkify from "linkify-react";

import DotClickMenuAtPost from "./DotClickMenuAtPost";
import ModalForm from "./ModalForm";
import PostCommentsCount from "./PostCommentsCount";
import LikeAction from "./LikeAction";
import DisplayPostOnDDP from "./DisplayPostOnDDP";
import PostCommentsPreview from "./PostCommentsPreview";
import GetUserPrefecture from "./GetUserPrefecture";
import ImageRenderer from "./ImageRenderer";
import PostComments from "./PostComments";
import InputPostComment from "./InputPostComment";
import DialogContentComponent from "./DialogContentComponent";

const DisplayPost = ({ post }) => {
  const linkifyOptions = useMemo(
    () => ({ className: "text-blue-400", target: "blank" }),
    []
  );

  const { id } = useParams();
  const { displayNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const segments = useMemo(() => location.pathname.split("/"), [location.pathname]);
  const firstSegment = segments[1];
  const showMoreLink = !location.pathname.includes("/post");
  const isKomebu = segments.includes("komebu");

  const [postor, setPostor] = useState(null);
  const [isAuthor, setIsAuthor] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);

  const [openModal, setOpen] = useState(false);
  const [form, setForm] = useState("");
  const [requestId, setRequestId] = useState("");

  const [photos, setPhotos] = useState([]);
  const [data, setData] = useState({ message: "", error: "", loading: false });
  const [showFullMessage, setShowFullMessage] = useState(true);

  const [openDialog, setOpenDialog] = useState(false);
  const [likeLoading, setLikeLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(true);

  // ★「非公開にしたら即消す」用（Hooks違反しないよう return 内で制御）
  const [isHidden, setIsHidden] = useState(false);

  // -------------------------
  // Effects（post / auth 未確定ガード）
  // -------------------------

  // 投稿者情報
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        if (!post?.creator) return;
        const snap = await getDoc(doc(db, "users", post.creator));
        if (cancelled) return;
        if (snap.exists()) setPostor({ id: snap.id, ...snap.data() });
      } catch (e) {
        // silent
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [post?.creator]);

  // 投稿画像取得（postId / 旧id 両対応）
  useEffect(() => {
    let cancelled = false;

    const fetchPostImages = async () => {
      const postId = post?.id;
      if (!postId) return;

      try {
        const q1 = query(collection(db, "postImage"), where("postId", "==", postId));
        const qs1 = await getDocs(q1);

        const q2 = query(collection(db, "postImage"), where("id", "==", postId));
        const qs2 = await getDocs(q2);

        const merged = [...qs1.docs, ...qs2.docs].reduce((acc, d) => {
          acc.set(d.id, d.data());
          return acc;
        }, new Map());

        const arr = Array.from(merged.values());
        if (!cancelled) setPhotos(arr);
      } catch (e) {
        if (!cancelled) setPhotos([]);
      }
    };

    fetchPostImages();
    return () => {
      cancelled = true;
    };
  }, [post?.id]);

  // author/admin 判定
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const uid = auth.currentUser?.uid;
        if (!uid || !post) return;

        if (!cancelled) setIsAuthor(post?.creator === uid);

        const userSnap = await getDoc(doc(db, "users", uid));
        if (cancelled) return;
        setIsAdmin(Boolean(userSnap.exists() && userSnap.data()?.isAdmin));
      } catch (e) {
        if (!cancelled) setIsAdmin(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [post]);

  // 現在ユーザー情報
  useEffect(() => {
    let cancelled = false;

    const fetchUserInfo = async () => {
      try {
        const uid = auth.currentUser?.uid;
        if (!uid) return;

        const userSnap = await getDoc(doc(db, "users", uid));
        if (cancelled) return;

        if (userSnap.exists()) setUser({ id: userSnap.id, ...userSnap.data() });
      } catch (e) {
        // silent
      }
    };

    fetchUserInfo();
    return () => {
      cancelled = true;
    };
  }, []);

  // メッセージ長で「もっと読む」制御
  useEffect(() => {
    if (!post?.message) return;
    const newlineCount = (post.message.match(/\n/g) || []).length;
    if (newlineCount > 5) setShowFullMessage(false);
    else setShowFullMessage(true);
  }, [post?.message, post?.id]);

  // DOM のオーバーフロー判定（targetElement を render 中に取らない）
  useEffect(() => {
    const postId = post?.id;
    if (!postId) return;

    const el = document.getElementById(postId);
    if (!el) return;

    // 次の描画フレームで計測
    const t = setTimeout(() => {
      try {
        if (el.scrollHeight > el.offsetHeight) setShowFullMessage(false);
      } catch (e) {
        // silent
      }
    }, 0);

    return () => clearTimeout(t);
  }, [post?.id, post?.message, showMoreLink]);

  // -------------------------
  // Handlers
  // -------------------------

  const handleMoveToUser = () => {
    if (!postor?.id) return;
    navigate("/user/" + postor.id);
  };

  const handleDialogOpen = () => {
    setOpenDialog(true);
    setLikeLoading(true);
    setCommentsLoading(true);
    setTimeout(() => {
      setLikeLoading(false);
      setCommentsLoading(false);
    }, 1000);
  };

  const handleDialogClose = () => setOpenDialog(false);

  // ModalForm を開く（編集など）
  const handleEdit = (e, postId) => {
    setOpen(true);
    setForm(e);
    setRequestId(postId);
  };

  // 既存 doRegist（残しておく：今回の目的は Hooks違反の解消）
  const doRegist = async (e) => {
    e.preventDefault();
    setData((prev) => ({ ...prev, error: "", loading: true }));

    try {
      const docRef = doc(db, "post", post?.id);
      await updateDoc(docRef, { updatedAt: serverTimestamp() });

      if (photos.length > 0) {
        const photoPromises = photos.map(async (file) => {
          const ImageRef = ref(storage, `feed/${post?.id} - ${file.file.name}`);
          const snap = await uploadBytes(ImageRef, file.file);
          const url = await getDownloadURL(ref(storage, snap.ref.fullPath));

          await addDoc(collection(db, "postImage"), {
            url,
            path: snap.ref.fullPath,
            postId: post.id,
            id: post.id,
            isPublish: true,
            creator: auth.currentUser.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            type: "post",
          });

          return { url, path: snap.ref.fullPath };
        });

        const uploadedPhotos = await Promise.all(photoPromises);
        setPhotos(uploadedPhotos);
      }

      setPhotos([]);
      displayNotification({ message: "投稿しました。", type: "success" });
      setData((prev) => ({ ...prev, error: null, loading: false }));
      setOpen(false);
      window.location.reload();
    } catch (err) {
      const errorData = { message: err.message, stack: err.stack };
      addDoc(collection(db, "error"), {
        createdAt: serverTimestamp(),
        error: errorData,
        component: "AddPost.js",
      });

      setData((prev) => ({ ...prev, error: err.message, loading: false }));
    }
  };

  // -------------------------
  // Render（Hooks違反しないよう、すべて return 内分岐）
  // -------------------------

  // post がまだない場合の保険（nullでも Hooks ルールは守れている）
  if (!post) return null;

  return isHidden ? null : post?.type === "ddp" ? (
    <DisplayPostOnDDP post={post} />
  ) : (
    <>
      <ModalForm
        form={form}
        setOpen={setOpen}
        open={openModal}
        requestId={requestId}
        target={post}
        // ★ここは ModalForm 側が受け取れるなら使える（受け取れなくても無害）
        onHidden={() => setIsHidden(true)}
        onEdit={handleEdit}
        onRegist={doRegist}
      />

      <div
        className="max-w-3xl mx-2 my-5"
        style={{
          filter: user?.isVillager || isKomebu ? "none" : "blur(4px)",
        }}
      >
        <div className="flex justify-between border-x border-t rounded-t-md p-3 hover:cursor-pointer duration-150 bg-white">
          <div className="flex space-x-2 items-center">
            <Avatar src={postor?.avatar} />
            <div>
              <div className="flex space-x-1 items-center">
                <div
                  className="font-bold whitespace-pre-wrap break-words line-clamp-1 cursor-pointer hover:underline"
                  onClick={handleMoveToUser}
                >
                  {(postor?.lastName || "") + (postor?.firstName || "")}
                </div>
                <div className="text-xs text-gray-500">
                  @<GetUserPrefecture userId={postor?.id} />
                </div>
              </div>

              {post?.updatedAt?.toDate && (
                <div className="text-gray-300 text-xs">
                  {formatDistanceToNow(post.updatedAt.toDate(), { locale: ja })}前
                </div>
              )}
            </div>
          </div>

          <div className="p-2">
            {(isAuthor || isAdmin) && (
              <DotClickMenuAtPost
                post={post}
                // ★DotClickMenuAtPost 側が受け取れるなら成功時に呼ぶ
                onHidden={() => setIsHidden(true)}
                onOpenModal={(f, pid) => handleEdit(f, pid)}
              />
            )}
          </div>
        </div>

        <div
          className={`relative px-2 pt-3 border-x duration-150 whitespace-pre-wrap break-all leading-normal ${
            showMoreLink ? "line-clamp-6" : ""
          } text-gray-600 bg-white`}
          id={post.id}
        >
          <Linkify options={linkifyOptions}>
            {String(post.message || "")
              .replace(/[ \t]+/g, "")
              .replace(/　/g, "")}
          </Linkify>
        </div>

        {!showFullMessage && showMoreLink && (
          <div className="border-r border-l bg-white flex justify-end items-center pr-4">
            <span
              className="w-fit h-5 text-gray-500 hover:font-bold hover:cursor-pointer hover:underline text-sm"
              onClick={handleDialogOpen}
            >
              もっと読む
            </span>
          </div>
        )}

        <div className="duration-150 border-x whitespace-pre-wrap break-words line-clamp-6 bg-white">
          <ImageRenderer images={photos} />
        </div>

        <div
          className={`${
            firstSegment !== "post" ? "  " : " "
          } p-2 duration-150 border-x bg-white justify-between grid grid-cols-2 gap-2`}
        >
          <LikeAction post={post} />
          <PostCommentsCount post={post} onClick={handleDialogOpen} />
        </div>

        {id && firstSegment === "post" && (
          <div className="group-hover:border-x-blue-500 duration-150 border-x whitespace-pre-wrap break-words bg-white h-auto">
            <PostComments post={post} />
          </div>
        )}

        {!id && (
          <div className="group-hover:border-x-blue-500 duration-150 border-x whitespace-pre-wrap break-all bg-white border-b rounded-b-md w-full max-w-full overflow-hidden">
            <PostCommentsPreview post={post} />
          </div>
        )}

        {id && firstSegment === "group" && (
          <div className="group-hover:border-x-blue-500 duration-150 border-x whitespace-pre-wrap break-all bg-white border-b rounded-b-md w-full max-w-full overflow-hidden">
            <PostCommentsPreview post={post} />
          </div>
        )}

        {firstSegment === "post" && (
          <div className="p-5 border-t rounded-b-md duration-150 border-x border-b whitespace-pre-wrap break-words line-clamp-6 bg-white">
            <InputPostComment post={post} />
          </div>
        )}

        {firstSegment === "user" && (
          <div className="border-x border-b rounded-b-md h-5 bg-white"></div>
        )}
      </div>

      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ style: { margin: 0, width: "100%" } }}
      >
        <DialogTitle>
          投稿の詳細
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContentComponent
          post={post}
          postor={postor}
          isAuthor={isAuthor}
          likeLoading={likeLoading}
          commentsLoading={commentsLoading}
          handleMoveToUser={handleMoveToUser}
        />
      </Dialog>
    </>
  );
};

export default memo(DisplayPost);
